import React from "react";
import { useTranslation } from "react-i18next";

function useCountUp(target, durationMs = 1200) {
    const [value, setValue] = React.useState(0);
    const ref = React.useRef(null);
    const startedRef = React.useRef(false);

    React.useEffect(() => {
        if (typeof window === "undefined" || !ref.current) return;
        const el = ref.current;
        const reduce =
            window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !startedRef.current) {
                        startedRef.current = true;
                        if (reduce) {
                            setValue(target);
                            return;
                        }
                        const start = performance.now();
                        function tick(now) {
                            const t = Math.min(1, (now - start) / durationMs);
                            const eased = 1 - Math.pow(1 - t, 3);
                            setValue(Math.round(eased * target));
                            if (t < 1) requestAnimationFrame(tick);
                        }
                        requestAnimationFrame(tick);
                    }
                });
            },
            { threshold: 0.3 },
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [target, durationMs]);

    return { ref, value };
}

function StatCard({ stat }) {
    const { ref, value } = useCountUp(stat.value);
    return (
        <div ref={ref} className="card-soft p-6 text-center">
            <div className="font-display text-4xl sm:text-5xl font-semibold text-[color:var(--brand-navy)]">
                {value.toLocaleString()}
                <span className="text-[color:var(--brand-teal-2)]">{stat.suffix}</span>
            </div>
            <p className="mt-2 text-sm text-[color:var(--text-muted)] leading-relaxed">{stat.label}</p>
        </div>
    );
}

export function StatsCounters() {
    const { t } = useTranslation();
    const STATS = [
        { value: 20, suffix: "+", label: t("stats.s1") },
        { value: 30000, suffix: "+", label: t("stats.s2") },
        { value: 100, suffix: "%", label: t("stats.s3") },
        { value: 24, suffix: "h", label: t("stats.s4") },
    ];
    return (
        <section data-testid="stats-counters" className="container-page section-y">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
                {STATS.map((s) => (
                    <StatCard key={s.label || s.value} stat={s} />
                ))}
            </div>
        </section>
    );
}
